import { Anagrafica } from '../_models/Anagrafica';
import { Sede } from '../_models/Sede';
import { CampoPropagabile, ModificaCampo, VALORE_VUOTO, modificheDi, norm } from './propagazione-comune';

// Feature 120 (issue #3368) — diff fra lo stato di apertura del modale anagrafica e quello alla
// conferma, per proporre la propagazione agli ordini clienti campo per campo.
//
// Helper PURO: nessuna dipendenza Angular, nessuna chiamata HTTP, nessuno stato. È il punto in cui
// si decide COSA mostrare nel dialogo; i VALORI propagati non passano di qui — il backend li rilegge
// dal proprio database (FR-030), e questi servono solo a mostrare "vecchio → nuovo" all'operatore.
//
// ── Mappa dei campi propagabili: TERZA delle tre copie ─────────────────────────────────────────
// 1) BackEnd/ArchiConnectSaleApi/src/.../Sale/Helpers/PropagazioneCampiOdV.cs (whitelist)
// 2) BackEnd/ArchiConnectRegistryApi/src/.../Registry/Services/AnagraficaPropagazioneService.cs
// 3) questo file (etichette e diff per il dialogo)
// Le `chiave` sono i nomi delle COLONNE DI DESTINAZIONE su OrdineVendita: è ciò che permette a
// Registry e Sale di validarle senza tradurre nulla. Chi ne cambia una qui deve allinearle tutte.

// Feature 121 (issue #3372) — il nucleo PURO (regola di uguaglianza, tipi del diff, calcolo delle
// modifiche) è stato estratto in `propagazione-comune.ts` e viene ri-esportato da qui: il
// comportamento è invariato, gli import esistenti continuano a funzionare e la suite
// `anagrafica-propagazione.spec.ts` resta verde SENZA modifiche — è il test dell'estrazione
// (spec FR-060, SC-011).
export { VALORE_VUOTO, norm };
export type { CampoPropagabile, ModificaCampo };

export interface ModificaCampoUtente extends ModificaCampo {
  propagabile: boolean;
  motivoNonPropagabile?: string;
}

// Il gruppo indirizzo ha UNA SOLA proprietà `selezionato` (FR-006, FR-024): le sue `modifiche` sono
// di sola lettura nel dialogo. Una selezione parziale produrrebbe un indirizzo di consegna
// contenente un valore che l'operatore ha esplicitamente rifiutato.
export interface GruppoModifiche {
  chiave: 'indirizzo';
  etichetta: string;
  modifiche: ModificaCampo[];
  selezionato: boolean;
}

export interface DiffSede {
  sedeId: number;
  nomeSede: string;
  gruppoIndirizzo?: GruppoModifiche;
  modifiche: ModificaCampo[];
  indirizzoConsegna?: string;
  eliminata: boolean;
}

export interface DiffPropagazione {
  anagrafica: ModificaCampo[];
  campiUtente: ModificaCampoUtente[];
  sedi: DiffSede[];
}

// Sedi create / modificate / eliminate nel modale e non ancora inviate al server (FR-015).
export interface SediPending {
  creates: Sede[];
  updates: Sede[];
  deletes: Sede[];
}

// I sei campi scalari del cliente (i campi utente hanno un percorso proprio).
export const CAMPI_ANAGRAFICA: CampoPropagabile<Anagrafica>[] = [
  { chiave: 'RagioneSocialeCliente', etichetta: 'Ragione sociale', getter: a => a.ragioneSociale },
  { chiave: 'RifCliente',            etichetta: 'Riferimento',     getter: a => a.riferimento },
  { chiave: 'Rappresentante',        etichetta: 'Rappresentante',  getter: a => a.rappresentante },
  { chiave: 'NotaCliente',           etichetta: 'Nota cliente',    getter: a => a.nota },
  { chiave: 'NotaConsegna',          etichetta: 'Nota di consegna', getter: a => a.notaConsegna },
  { chiave: 'NotaAggiuntiva',        etichetta: 'Nota aggiuntiva', getter: a => a.notaAggiuntiva },
];

// Unica definizione del gruppo indirizzo lato frontend (FR-024).
export const CAMPI_GRUPPO_INDIRIZZO = ['Indirizzo', 'Citta', 'Provincia'];

export const CAMPI_SEDE: CampoPropagabile<Sede, 'indirizzo'>[] = [
  { chiave: 'Indirizzo',   etichetta: 'Indirizzo',   gruppo: 'indirizzo', getter: s => s.indirizzo },
  { chiave: 'Citta',       etichetta: 'Città',       gruppo: 'indirizzo', getter: s => s.citta },
  { chiave: 'Provincia',   etichetta: 'Provincia',   gruppo: 'indirizzo', getter: s => s.provincia },
  { chiave: 'CAP',         etichetta: 'CAP',         getter: s => s.cap },
  { chiave: 'Nazione',     etichetta: 'Nazione',     getter: s => s.nazione },
  { chiave: 'Zona',        etichetta: 'Zona',        getter: s => s.zona },
  { chiave: 'Riferimento', etichetta: 'Riferimento', getter: s => s.riferimento },
  { chiave: 'Telefono',    etichetta: 'Telefono',    getter: s => s.telefono },
  { chiave: 'Email',       etichetta: 'Email',       getter: s => s.email },
];

// FR-007 (`null` ≡ `''` ≡ spazi), VALORE_VUOTO e `modificheDi` vivono ora in
// `propagazione-comune.ts`: stessa regola, stesso comportamento, un solo punto di correzione.

// Regola identica a ordine-vendita-create.component.ts (onSedeSelezionata): è la stessa che ha
// prodotto il valore attuale sull'ordine alla creazione.
export function indirizzoConsegna(sede: Sede): string {
  return [sede.indirizzo, sede.citta, sede.provincia].filter(v => !!v).join(', ');
}

// Diff dell'anagrafica e delle sue sedi. Le sedi CREATE nella sessione sono fuori dal diff per
// costruzione (nessun ordine le referenzia); quelle ELIMINATE entrano marcate, senza modifiche
// selezionabili, perché il dialogo deve avvisare che le righe collegate conservano l'indirizzo
// attuale (FR-017).
// Motivi per cui un campo utente dell'anagrafica non è propagabile all'ordine (FR-021).
export const MOTIVO_SENZA_OMONIMO = 'nessun campo utente con lo stesso codice sull\'ordine di vendita';
export const MOTIVO_DEFINIZIONI_NON_DISPONIBILI =
  'definizioni dei campi utente non disponibili in questo momento';

function valoriCampiUtente(json: string | null | undefined): Record<string, string> {
  if (!json) return {};
  try {
    const valori = JSON.parse(json);
    return valori && typeof valori === 'object' ? valori : {};
  } catch {
    return {};
  }
}

// Diff dei campi utente per CODICE OMONIMO fra le definizioni dell'anagrafica e quelle dell'ordine
// di vendita, confronto insensibile alle maiuscole (FR-020). L'etichetta e la grafia mostrate sono
// quelle dell'anagrafica; la grafia canonica scritta sull'ordine la decide il backend.
//
// `codiciOrdineVendita` a `null` = definizioni NON DISPONIBILI: i campi utente diventano tutti non
// propagabili con motivo esplicito, e i campi scalari restano propagabili (FR-021, US3/5).
export function calcolaDiffCampiUtente(
  prima: Anagrafica, dopo: Anagrafica, codiciOrdineVendita: string[] | null,
): ModificaCampoUtente[] {
  const valoriPrima = valoriCampiUtente(prima.campiUtente);
  const valoriDopo = valoriCampiUtente(dopo.campiUtente);
  const codici = [...new Set([...Object.keys(valoriPrima), ...Object.keys(valoriDopo)])];
  const omonimi = new Set((codiciOrdineVendita ?? []).map(c => c.toUpperCase()));

  return codici
    .filter(codice => norm(valoriPrima[codice]) !== norm(valoriDopo[codice]))
    .map(codice => modificaCampoUtente(codice, valoriPrima, valoriDopo, codiciOrdineVendita, omonimi));
}

function modificaCampoUtente(
  codice: string, prima: Record<string, string>, dopo: Record<string, string>,
  codiciOrdineVendita: string[] | null, omonimi: Set<string>,
): ModificaCampoUtente {
  const propagabile = codiciOrdineVendita !== null && omonimi.has(codice.toUpperCase());
  const motivo = codiciOrdineVendita === null
    ? MOTIVO_DEFINIZIONI_NON_DISPONIBILI
    : MOTIVO_SENZA_OMONIMO;
  return {
    chiave: codice,
    etichetta: codice,
    vecchio: norm(prima[codice]) || VALORE_VUOTO,
    nuovo: norm(dopo[codice]) || VALORE_VUOTO,
    selezionato: propagabile,
    propagabile,
    motivoNonPropagabile: propagabile ? undefined : motivo,
  };
}

export function calcolaDiff(
  prima: Anagrafica, dopo: Anagrafica, sediPending: SediPending,
  codiciOrdineVendita: string[] | null = [],
): DiffPropagazione {
  const sediPrima = new Map((prima.sedi ?? []).map(s => [s.id, s]));
  const sedi = sediPending.updates
    .map(sede => diffSede(sediPrima.get(sede.id), sede))
    .filter((d): d is DiffSede => d !== null);

  const eliminate = sediPending.deletes
    .filter(sede => sede.id > 0)
    .map(sede => ({ sedeId: sede.id, nomeSede: nomeSede(sede), modifiche: [], eliminata: true }));

  return {
    anagrafica: modificheDi(CAMPI_ANAGRAFICA, prima, dopo),
    campiUtente: calcolaDiffCampiUtente(prima, dopo, codiciOrdineVendita),
    sedi: [...sedi, ...eliminate],
  };
}

function diffSede(prima: Sede | undefined, dopo: Sede): DiffSede | null {
  // Sede non presente all'apertura: è stata creata in questa sessione, nessun ordine la referenzia.
  if (!prima || dopo.id <= 0) return null;

  const tutte = modificheDi(CAMPI_SEDE, prima, dopo);
  const nelGruppo = tutte.filter(m => CAMPI_GRUPPO_INDIRIZZO.includes(m.chiave));
  const modifiche = tutte.filter(m => !CAMPI_GRUPPO_INDIRIZZO.includes(m.chiave));
  if (tutte.length === 0) return null;

  return {
    sedeId: dopo.id,
    nomeSede: nomeSede(dopo),
    // Un gruppo senza modifiche non viene prodotto, e con esso non compare la riga
    // "Indirizzo di consegna (ricalcolato)" (FR-024, scenario US2/2).
    gruppoIndirizzo: nelGruppo.length === 0
      ? undefined
      : { chiave: 'indirizzo', etichetta: 'Indirizzo', modifiche: nelGruppo, selezionato: true },
    modifiche,
    indirizzoConsegna: nelGruppo.length === 0 ? undefined : indirizzoConsegna(dopo),
    eliminata: false,
  };
}

function nomeSede(sede: Sede): string {
  return norm(sede.nome) || norm(sede.codice) || `Sede ${sede.id}`;
}

// Vero quando c'è almeno una modifica propagabile: è la prima delle due condizioni che aprono il
// dialogo (FR-002).
export function haModifichePropagabili(diff: DiffPropagazione): boolean {
  return diff.anagrafica.length > 0
    || diff.campiUtente.some(c => c.propagabile)
    || diff.sedi.some(s => !s.eliminata && (s.modifiche.length > 0 || !!s.gruppoIndirizzo));
}
